'use client';

import type { Column } from '@tanstack/react-table';
import { PlusCircle, XCircle } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import type { Option } from '@/types/data-table';

interface DataTableFacetedFilterProps<TData, TValue> {
  column?: Column<TData, TValue>;
  title?: string;
  options: Option[];
  multiple?: boolean;
}

export function DataTableFacetedFilter<TData, TValue>({
  column,
  title,
  options,
  multiple,
}: DataTableFacetedFilterProps<TData, TValue>) {
  const { t } = useTranslation('common');
  const [open, setOpen] = React.useState(false);

  const columnFilterValue = column?.getFilterValue();
  const selectedValues = new Set(Array.isArray(columnFilterValue) ? columnFilterValue : []);

  // Get faceted unique values for counts
  const facetedUniqueValues = column?.getFacetedUniqueValues();

  // Enhance options with counts from faceted values
  // When options are explicitly provided (server-side), show all of them
  // When using client-side faceting, filter by count
  const optionsWithCounts = React.useMemo(() => {
    if (!facetedUniqueValues) return options;

    return options.map((option) => ({
      ...option,
      count: facetedUniqueValues.get(option.value) || 0,
    }));
  }, [options, facetedUniqueValues]);

  // Check if we have explicit options (server-side) - computed from optionsWithCounts
  const hasExplicitOptions = options.length > 0;

  const onItemSelect = React.useCallback(
    (option: Option, isSelected: boolean) => {
      if (!column) return;

      if (multiple) {
        const newSelectedValues = new Set(selectedValues);
        if (isSelected) {
          newSelectedValues.delete(option.value);
        } else {
          newSelectedValues.add(option.value);
        }
        const filterValues = Array.from(newSelectedValues);
        column.setFilterValue(filterValues.length ? filterValues : undefined);
      } else {
        column.setFilterValue(isSelected ? undefined : [option.value]);
        setOpen(false);
      }
    },
    [column, multiple, selectedValues],
  );

  const onReset = React.useCallback(
    (event?: React.MouseEvent) => {
      event?.stopPropagation();
      column?.setFilterValue(undefined);
    },
    [column],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="border-dashed">
          {selectedValues?.size > 0 ? (
            <div
              role="button"
              aria-label={t('dataTable.filters.clearFilterAria', { title: title ?? '' })}
              tabIndex={0}
              onClick={onReset}
              className="focus-visible:ring-ring rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-1"
            >
              <XCircle />
            </div>
          ) : (
            <PlusCircle />
          )}
          {title}
          {selectedValues?.size > 0 && (
            <>
              <Separator orientation="vertical" className="mx-0.5 data-[orientation=vertical]:h-4" />
              <Badge variant="secondary" className="rounded-sm px-1 font-normal lg:hidden">
                {selectedValues.size}
              </Badge>
              <div className="hidden items-center gap-1 lg:flex">
                {selectedValues.size > 2 ? (
                  <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                    {t('dataTable.filters.countSelected', { count: selectedValues.size })}
                  </Badge>
                ) : (
                  optionsWithCounts
                    .filter((option) => selectedValues.has(option.value))
                    .map((option) => (
                      <Badge variant="secondary" key={option.value} className="rounded-sm px-1 font-normal">
                        {option.label}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[12.5rem] p-0" align="start">
        <Command>
          <CommandInput placeholder={title} />
          <CommandList className="max-h-full">
            <CommandEmpty>{t('dataTable.filters.noResultsFound')}</CommandEmpty>
            <CommandGroup className="max-h-[18.75rem] overflow-y-auto overflow-x-hidden">
              {optionsWithCounts
                .filter(
                  (opt) =>
                    // If options are explicitly provided (server-side), show all of them
                    hasExplicitOptions ||
                    // Otherwise, only show options with data or that are selected
                    !facetedUniqueValues ||
                    (opt.count ?? 0) > 0 ||
                    selectedValues.has(opt.value),
                )
                .map((option) => {
                  const isSelected = selectedValues.has(option.value);

                  return (
                    <CommandItem key={option.value} onSelect={() => onItemSelect(option, isSelected)}>
                      <Checkbox checked={isSelected} />
                      {option.icon && <option.icon />}
                      <span className="truncate">{option.label}</span>
                      {/* Only show counts for client-side faceting (when options are auto-generated) */}
                      {!hasExplicitOptions && option.count && option.count > 0 && (
                        <span className="ml-auto font-mono text-xs">{option.count}</span>
                      )}
                    </CommandItem>
                  );
                })}
            </CommandGroup>
            {selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem onSelect={() => onReset()} className="justify-center text-center">
                    {t('dataTable.filters.clearFilters')}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
